/**
 * The `25% 50% 75% MAX` row.
 *
 * MAX is the same control as the percent chips, at its last stop — it reports
 * 100, and the caller resolves that against the field's maximum. That is what
 * keeps the chip that wrote a value lit when the field holds it.
 *
 * When the maximum is not known, the row does not quietly fill 0: every chip is
 * `aria-disabled` and points at the reason. `aria-disabled`, never `disabled`,
 * because a control nobody can focus is a control that never gets to explain
 * itself — the trader tabs to it and hears "your balance hasn't loaded yet"
 * instead of clicking a dead button.
 */
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { MaxResult } from "../../../utils/amount/max/types";
import { ActionRow, Chip, HintText } from "./style";

/** MAX is a stop like any other, so one code path fills the field. */
export const MAX_PERCENT = 100;

export interface QuickActionsProps {
  /** Stops before MAX. */
  presets?: number[];
  showMax?: boolean;
  /** The lit stop — `activePreset()` from `utils/amount/percent`. */
  active: number | null;
  onSelect: (pct: number) => void;
  /**
   * The field's maximum. Omit it where there is no maximum in play (a slider
   * that is only ever a percentage); an unavailable one disables the row.
   */
  max?: MaxResult;
  disabled?: boolean;
  /** Id of a reason element the caller already renders (a tooltip). */
  unavailableTooltipId?: string;
}

export const QuickActions = ({
  presets = [25, 50, 75],
  showMax = true,
  active,
  onSelect,
  max,
  disabled,
  unavailableTooltipId,
}: QuickActionsProps) => {
  const { t } = useTranslation("app", { keyPrefix: "common.inputs" });
  const generatedId = useId();

  // No `max` at all is "no opinion", not "unavailable": a percent-only row
  // still works.
  const reason = max && !max.ok ? max.reason : undefined;
  const unavailable = reason !== undefined;
  const inert = Boolean(disabled) || unavailable;

  const hintId = unavailableTooltipId ?? `${generatedId}-max-unavailable`;
  const ownHint = unavailable && unavailableTooltipId === undefined;

  const stops = showMax
    ? [
        ...presets.filter((pct) => pct !== MAX_PERCENT),
        MAX_PERCENT,
      ].map((pct) => ({ pct, label: pct === MAX_PERCENT ? t("max") : `${pct}%` }))
    : presets.map((pct) => ({ pct, label: `${pct}%` }));

  return (
    <>
      <ActionRow>
        {stops.map(({ pct, label }) => (
          <Chip
            key={pct}
            type="button"
            $active={active === pct}
            aria-pressed={active === pct}
            aria-disabled={inert || undefined}
            aria-describedby={unavailable ? hintId : undefined}
            onClick={() => {
              if (!inert) onSelect(pct);
            }}
          >
            {label}
          </Chip>
        ))}
      </ActionRow>
      {ownHint ? (
        <HintText id={hintId} role="note">
          {t(`max_unavailable.${reason}`)}
        </HintText>
      ) : null}
    </>
  );
};
